import { DocumentActionComponent, useDocumentOperation } from 'sanity'

/**
 * Custom publish action that auto-increments the version for aiGenerationRules documents.
 * Version format: X.Y where Y auto-increments on each publish (1.0 → 1.1 → 1.2...)
 */
export function createAutoVersionPublishAction(
  originalPublishAction: DocumentActionComponent
): DocumentActionComponent {
  const AutoVersionPublishAction: DocumentActionComponent = (props) => {
    const { patch, publish } = useDocumentOperation(props.id, props.type)
    const originalResult = originalPublishAction(props)

    // Only modify for aiGenerationRules documents
    if (props.type !== 'aiGenerationRules') {
      return originalResult
    }

    if (!originalResult) {
      return null
    }

    return {
      ...originalResult,
      onHandle: async () => {
        const currentVersion = (props.draft?.version || props.published?.version || '1.0') as string
        const newVersion = incrementMinorVersion(currentVersion)
        const now = new Date().toISOString()

        // Patch the version and lastUpdated before publishing
        patch.execute([
          { set: { version: newVersion } },
          { set: { lastUpdated: now } },
        ])

        // Wait a tick for the patch to apply
        await new Promise((resolve) => setTimeout(resolve, 100))

        // Call original publish
        publish.execute()

        // Call original onHandle if it exists
        if (originalResult.onHandle) {
          originalResult.onHandle()
        }
      },
      label: originalResult.label || 'Publish',
      title: `Publish with auto-incremented version (current: ${props.draft?.version || props.published?.version || '1.0'})`,
    }
  }

  return AutoVersionPublishAction
}

/**
 * Increment minor version: 1.0 → 1.1, 1.9 → 1.10, etc.
 */
function incrementMinorVersion(version: string): string {
  const parts = version.split('.')
  if (parts.length !== 2) {
    return '1.1' // Reset to 1.1 if format is invalid
  }

  const major = parseInt(parts[0], 10) || 1
  const minor = parseInt(parts[1], 10) || 0

  return `${major}.${minor + 1}`
}
